import React, { useMemo, useState } from 'react';
import { FEATURES } from '../constants/features';
import {
  FISHIQ_PUBLIC_SOURCE_REGISTRY_VERSION,
  fishIQPublicSourceKeysForSpecies,
  getFishIQPublicSources,
} from '../constants/fishIQPublicSources';
import type { FishIQPublicSourceRef } from '../types';

interface FishIQSourceBadgeProps {
  species?: any;
  isDarkMode?: boolean;
  compact?: boolean;
  className?: string;
}

const cx = (...parts: Array<string | false | null | undefined>) => parts.filter(Boolean).join(' ');

const visibleSource = (source: FishIQPublicSourceRef) => {
  // Do not show unresolved source families as real citations.
  if (source.id === 'state_agency_regulations_ready') return false;
  return true;
};

const providerLabel = (provider: string) => {
  if (provider === 'roddo_editorial') return 'Roddo';
  if (provider === 'gbif') return 'GBIF';
  if (provider === 'noaa') return 'NOAA';
  if (provider === 'usgs') return 'USGS';
  if (provider === 'state_agency') return 'Agency';
  return 'Public';
};

const statusLabel = (source: FishIQPublicSourceRef) => {
  if (source.provider === 'roddo_editorial') return 'seed';
  if (source.registryStatus === 'active') return 'active';
  return 'ready';
};

const sourceTone = (isDarkMode: boolean) => ({
  shell: {
    background: isDarkMode
      ? 'rgba(103,216,232,0.08)'
      : 'rgba(47,128,237,0.07)',
    borderColor: isDarkMode ? 'rgba(103,216,232,0.16)' : 'rgba(47,128,237,0.18)',
    color: isDarkMode ? '#BDEFF5' : '#2469B2',
    boxShadow: isDarkMode
      ? '0 6px 16px rgba(0,0,0,0.14), inset 0 1px 0 rgba(255,255,255,0.06)'
      : '0 5px 14px rgba(45,88,130,0.07), inset 0 1px 0 rgba(255,255,255,0.82)',
  },
  panel: {
    background: isDarkMode ? '#0F1828' : 'rgba(255,255,255,0.82)',
    borderColor: isDarkMode ? 'rgba(148,166,201,0.08)' : 'rgba(214,229,241,0.95)',
    color: isDarkMode ? 'rgba(235,243,255,0.88)' : '#526271',
  },
  muted: isDarkMode ? 'rgba(230,238,255,0.62)' : '#7A8794',
});

const FishIQSourceBadge: React.FC<FishIQSourceBadgeProps> = ({
  species,
  isDarkMode = false,
  compact = false,
  className,
}) => {
  const [open, setOpen] = useState(false);

  const sources = useMemo(() => {
    if (!species || !Boolean((FEATURES as any).FISHIQ_PUBLIC_SOURCE_REGISTRY)) return [];
    const explicitKeys = Array.isArray(species?.publicSourceRefs) ? species.publicSourceRefs : [];
    const fallbackKeys = explicitKeys.length ? explicitKeys : fishIQPublicSourceKeysForSpecies(species);
    return getFishIQPublicSources(fallbackKeys).filter(visibleSource);
  }, [species]);

  if (!sources.length) return null;

  const tone = sourceTone(isDarkMode);
  const editorialCount = sources.filter((source) => source.provider === 'roddo_editorial').length;
  const publicReadyCount = sources.filter((source) => source.provider !== 'roddo_editorial').length;
  const label = compact
    ? `${publicReadyCount ? `${publicReadyCount} public-ready` : 'Registry'}${editorialCount ? ' + Roddo' : ''}`
    : `Source registry ${FISHIQ_PUBLIC_SOURCE_REGISTRY_VERSION}`;

  return (
    <div className={cx('relative', className)}>
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          setOpen((prev) => !prev);
        }}
        className="inline-flex max-w-full items-center gap-2 rounded-[12px] border px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.13em] transition-all hover:-translate-y-[1px] active:translate-y-[1px]"
        style={tone.shell}
        aria-expanded={open}
        aria-label="Show source registry metadata"
        title="Static registry metadata only — no live API call"
      >
        <span className="truncate">{label}</span>
      </button>

      {open ? (
        <div
          className="mt-2 rounded-[14px] border p-3 text-left"
          style={tone.panel}
          onClick={(event) => event.stopPropagation()}
        >
          <div className="text-[10px] font-black uppercase tracking-[0.16em]" style={{ color: tone.muted }}>
            Static metadata • no tap fetch
          </div>
          <div className="mt-2 space-y-2">
            {sources.slice(0, 5).map((source) => (
              <div key={source.id} className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="truncate text-[12px] font-black" style={{ color: isDarkMode ? '#F8FBFF' : '#2F4152' }}>
                    {source.title}
                  </div>
                  <div className="mt-0.5 truncate text-[11px] font-semibold" style={{ color: tone.muted }}>
                    {source.publisher || providerLabel(source.provider)}
                  </div>
                </div>
                <span
                  className="shrink-0 rounded-full border px-2 py-0.5 text-[9px] font-black uppercase tracking-[0.12em]"
                  style={{
                    borderColor: isDarkMode ? 'rgba(148,166,201,0.08)' : 'rgba(196,213,229,0.92)',
                    color: tone.muted,
                  }}
                >
                  {providerLabel(source.provider)} · {statusLabel(source)}
                </span>
              </div>
            ))}
          </div>
          <p className="mt-2 text-[11px] font-semibold leading-5" style={{ color: tone.muted }}>
            These are registry links/readiness markers, not live confidence claims or real-time regulation checks.
          </p>
        </div>
      ) : null}
    </div>
  );
};

export default FishIQSourceBadge;
